import type { BillingCaseRowItem, CaseStatus } from "../types";
import type { AssignBillingGroupFormValues } from "./AssignBillingGroupModal/types";
import type { CaseEntityRow } from "./CaseEntitiesGrid/types";
import type { ReassignBillingGroupFormValues } from "./ReassignBillingGroupModal/types";
import type { EumCalcEditValues } from "./utils/eumProposedChangesBuilders";

export type CaseDetailsDrawerProps = {
  billingCase: BillingCaseRowItem | undefined;
  onClose: () => void;
  isChecker?: boolean;
  pendingChangeRequestIds?: string[];
};

export enum CaseTypeDisplay {
  ClientInformation = "Change in client information",
  FirstCapitalCallDate = "Trigger Date",
}

export enum CaseDetailsTabType {
  Entities = "Entities",
  Log = "Log",
}

export type CaseEntityRowItem = {
  billingGroup: string | null;
  entity: string;
  eum: number | null;
  entityId: number | null;
  jsqProductLine: string | null;
  proposedChanges: number;
};

export type ProposedChangeDetail = {
  label: string;
  currentValues: string[];
  proposedValues: string[];
};

export type ProposedChangeMetadata = {
  reasonCode?: string;
  details?: string;
  supportingDocumentName?: string;
  s3DocumentKey?: string;
};

export type EntityRowNode = {
  group: boolean | undefined;
  data: CaseEntityRow | undefined;
  key?: string | null;
};

export enum EntitySelectionType {
  None = "NONE",
  UnassignedEntity = "UNASSIGNED_ENTITY",
  AssignedEntity = "ASSIGNED_ENTITY",
  BillingGroup = "BILLING_GROUP",
}

export enum ReviewerConclusion {
  Approved = "APPROVED",
  Invalid = "INVALID",
}

export type CreateChangeRequestData = {
  entityIds: number[];
  entityNames: string[];
  formValues: AssignBillingGroupFormValues | ReassignBillingGroupFormValues;
  familyDisplayName: string;
};

export type RemoveFromBillingGroupData = {
  action: "remove";
  entityIds: number[];
  entityNames: string[];
  billingGroupId: string;
  billingGroupName: string;
};

export type MarkAsStandaloneData = {
  action: "standalone";
  entityIds: number[];
  entityNames: string[];
};

export type RenameBillingGroupData = {
  action: "rename";
  billingGroupId: string;
  billingGroupName: string;
  newBillingGroupName: string;
};

export type RemoveBillingGroupData = {
  action: "remove_group";
  billingGroupId: string;
  billingGroupName: string;
  entityNames: string[];
};

export type EumValueOverrideChangeData = {
  action: "eum_value_override";
  entityIds: number[];
  entityNames: string[];
  currentEumValue: number | null;
  proposedEumValue: number;
  reasonCode: string;
  description: string;
  file: File | null;
};

export type EumCalcEditChangeData = {
  action: "eum_calc_edit";
  entityIds: number[];
  entityNames: string[];
  current: EumCalcEditValues;
  proposed: EumCalcEditValues;
  reasonCode: string;
  description: string;
  file: File | null;
};

export type BulkActionChangeData =
  | CreateChangeRequestData
  | RemoveFromBillingGroupData
  | MarkAsStandaloneData
  | RenameBillingGroupData
  | RemoveBillingGroupData
  | EumValueOverrideChangeData
  | EumCalcEditChangeData;

export type SubmittedPendingChange = {
  changeRequestId: string;
  billingCaseId: number;
  status: CaseStatus;
};

export type SubmitGpEumChangesArgs = {
  billingApproverId: number;
  csmManagerApproverId: number;
};

export type EntitiesTabActions = {
  createAllPendingChanges: () => Promise<SubmittedPendingChange[]>;
  submitAllPendingEumChanges: (
    args: SubmitGpEumChangesArgs,
  ) => Promise<SubmittedPendingChange[]>;
};

export type StoredProposedChange = {
  changeRequestId: string;
  billingGroupName: string;
  billingGroupId?: string;
  entityIds: number[];
  detail: ProposedChangeDetail;
  metadata?: ProposedChangeMetadata;
};

export type StoredFamilyProposedChange = {
  changeRequestId: string;
  familyId: string;
  familyName: string;
  currentEntities: string[];
  proposedEntities: string[];
};

export type ProposedChangesResult = {
  entityMap: Map<number, StoredProposedChange[]>;
  entityCounts: Map<number, number>;
  familyMap: Map<string, StoredFamilyProposedChange>;
  familyCounts: Map<string, number>;
  billingGroupChanges: Map<string, StoredProposedChange[]>;
};

export type PendingFCCChange = {
  entityIds: number[];
  entityNames: string[];
  proposedDate: string;
};

export type GridActions = {
  deleteProposedChanges: (entityIds: number[]) => void;
};
